import firebase, { firebaseLooper, storageRef } from "../services/firebase";
import md5 from "md5";

class UserService {
    usersRef = firebase.database().ref("users");
    connectedRef = firebase.database().ref(".info/connected");
    presenceRef = firebase.database().ref("presence");

    async registerUser({ email, password, username }) {
        const user = await firebase
            .auth()
            .createUserWithEmailAndPassword(email, password)
            .then(({ user }) => user);

        await user.updateProfile({
            displayName: username,
            photoURL: `https://www.gravatar.com/avatar/${md5(user.email)}?d=identicon`,
        });

        await this.usersRef.child(user.uid).set({ name: user.displayName, avatar: user.photoURL });

        return user;
    }

    async loginUser({ email, password }) {
        return await firebase
            .auth()
            .signInWithEmailAndPassword(email, password)
            .then(({ user }) => user);
    }

    async logoutUser() {
        await firebase.auth().signOut();
    }

    async getUsers() {
        return await this.usersRef.once("value").then(users =>
            firebaseLooper(users.toJSON())
                .filter(({ id }) => id !== this.currentUser?.uid)
                .map(user => ({ ...user, status: "offline" })),
        );
    }

    setConnectionInfoListener(actionCallback) {
        this.connectedRef.on("value", actionCallback);
    }

    setPresenceListener(eventType, actionCallback) {
        this.presenceRef.on(eventType, actionCallback);
    }

    updateUsersStatus(users, userId, connected = true) {
        return users.map(user => (user.id === userId ? { ...user, status: connected ? "online" : "offline" } : user));
    }

    async updateUserWithStarredChannels(type, channel) {
        type === "star"
            ? await this.usersRef.child(`${this.currentUser.uid}/starred`).update({ [channel.id]: { ...channel } })
            : await this.usersRef.child(`${this.currentUser.uid}/starred`).child(channel.id).remove();

        return { type, channel };
    }

    async getUserStarredChannels() {
        return await this.usersRef
            .child(`${this.currentUser?.uid}/starred`)
            .once("value")
            .then(snap => firebaseLooper(snap.toJSON()));
    }

    async removeStarredChannelFromAllUsers(channelId) {
        const users = await this.getUsers();

        users.forEach(async user => await this.usersRef.child(`${user.id}/starred`).child(channelId).remove());
    }

    async setCurrentUserFavoriteColor(colors) {
        const { key } = await this.usersRef.child(`${this.currentUser.uid}/colors`).push();

        await this.usersRef.child(`${this.currentUser.uid}/colors`).child(key).update(colors);

        return { id: key, ...colors };
    }

    async removeCurrentUserFavoriteColor(id) {
        await this.usersRef.child(`${this.currentUser.uid}/colors`).child(id).remove();

        return { id };
    }

    async getCurrentUserFavoriteColors() {
        return await this.usersRef
            .child(`${this.currentUser.uid}/colors`)
            .once("value")
            .then(snap => firebaseLooper(snap.toJSON()));
    }

    async changeCurrentUserAvatar(data, storageDir) {
        let newAvatar = await storageRef(storageDir)
            .put(data.blob, data.metadata)
            .then(snap => snap.ref.getDownloadURL());

        await this.currentUser.updateProfile({ photoURL: newAvatar });

        await this.usersRef.child(this.currentUser.uid).update({ avatar: newAvatar });

        return newAvatar;
    }

    get currentUser() {
        return firebase.auth().currentUser;
    }
}

const userService = new UserService();

export default userService;
